import { defineStore } from 'pinia'

type CompareType = 'credits' | 'deposits' | 'cards'

const STORAGE_KEYS: Record<CompareType, string> = {
  credits: 'compare_credits',
  deposits: 'compare_deposits',
  cards: 'compare_cards'
}

const MAX_ITEMS = 10
const TTL_MS = 24 * 60 * 60 * 1000

type StorageEntry = { ids: number[]; expires: number }

const loadFromStorage = (type: CompareType): number[] => {
  if (!import.meta.client) return []
  try {
    const raw = localStorage.getItem(STORAGE_KEYS[type])
    if (!raw) return []
    const parsed: StorageEntry = JSON.parse(raw)
    if (!Array.isArray(parsed.ids) || Date.now() > parsed.expires) {
      localStorage.removeItem(STORAGE_KEYS[type])
      return []
    }
    return parsed.ids.map(Number).filter((n) => Number.isFinite(n) && n > 0)
  } catch {
    return []
  }
}

const saveToStorage = (type: CompareType, ids: number[]) => {
  if (!import.meta.client) return
  const entry: StorageEntry = { ids, expires: Date.now() + TTL_MS }
  try {
    localStorage.setItem(STORAGE_KEYS[type], JSON.stringify(entry))
  } catch {
    // Storage can be blocked by the browser or full; the list then lives in memory only
  }
}

export const useCompareStore = defineStore('compare', {
  state: () => ({
    creditsIds: [] as number[],
    depositsIds: [] as number[],
    cardsIds: [] as number[]
  }),

  getters: {
    totalCount: (state) =>
      state.creditsIds.length + state.depositsIds.length + state.cardsIds.length,

    countByType: (state) => ({
      credits: state.creditsIds.length,
      deposits: state.depositsIds.length,
      cards: state.cardsIds.length
    }),

    idsForType: (state) => (type: CompareType) => {
      if (type === 'credits') return state.creditsIds
      if (type === 'deposits') return state.depositsIds
      return state.cardsIds
    }
  },

  actions: {
    initFromStorage() {
      this.creditsIds = loadFromStorage('credits')
      this.depositsIds = loadFromStorage('deposits')
      this.cardsIds = loadFromStorage('cards')
    },

    _getList(type: CompareType): number[] {
      if (type === 'credits') return this.creditsIds
      if (type === 'deposits') return this.depositsIds
      return this.cardsIds
    },

    _setList(type: CompareType, ids: number[]) {
      if (type === 'credits') this.creditsIds = ids
      else if (type === 'deposits') this.depositsIds = ids
      else this.cardsIds = ids
      saveToStorage(type, ids)
    },

    has(type: CompareType, id: number): boolean {
      return this._getList(type).includes(id)
    },

    add(type: CompareType, id: number) {
      const list = this._getList(type)
      if (list.includes(id) || list.length >= MAX_ITEMS) return
      this._setList(type, [...list, id])
      useAnalytics().trackEvent('add_to_compare', { product_type: type, product_id: id })
    },

    remove(type: CompareType, id: number) {
      this._setList(type, this._getList(type).filter((i) => i !== id))
    },

    clear(type: CompareType) {
      this._setList(type, [])
    },

    setIds(type: CompareType, ids: number[]) {
      this._setList(type, ids.slice(0, MAX_ITEMS))
    }
  }
})
